import React, { useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import axios from "axios";
import * as ActionCable from '@rails/actioncable'
import CredentialWidget from './widgets/CredentialWidget'
import UploadWidget from './widgets/UploadWidget'
import { formatTimestamp } from './utils'

const FundsVerificationWidget = ({
  identifier,
  uploadButtonId,
  uploadWidgetScriptUrl,
  uploadWidgetStylesheetUrl,
  captureButtonId,
  captureWidgetScriptUrl,
  captureWidgetStylesheetUrl,
  mode
}) => {
  const isAffiliateMode = mode === 'affiliate'
  const legalUrl = (path) => {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}${path}`
    }
    return path
  }

  if (!identifier){
    return null
  }

  const [step, setStep] = useState(0);
  const [credentialLink, setCredentialLink] = useState(null);
  const [uploadLink, setUploadLink] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [externalIdentifier, setExternalIdentifier] = useState(null);
  const [underReview, setUnderReview] = useState(false);
  const lastSubmittedSessionRef = React.useRef(null);

  // if there are existing accounts or after a new submission
  const [showSubmitBtn, setShowSubmitBtn] = useState(false); 

  window.App || (window.App = {});
  window.App.cable = ActionCable.createConsumer();

  useEffect(() => {
    getIframe();

    App.cable.subscriptions.create({ channel: "BankingChannel", id: identifier, user_type: 'financial_user' }, {
      connected() {
        console.log("Connected to the channel:", this);
      },
      disconnected() {
        console.log("Disconnected");
      },
      received(data) {
        if (data.account_list) {
          setAccounts((prev) => [...data.account_list, ...prev])
          setShowSubmitBtn(true)
        }
        if (typeof data.under_review !== 'undefined') {
          setUnderReview(!!data.under_review)
        }
      }
    });

    const handleDiroMessage = (evt) => {
      const allowedOrigins = new Set(['https://session.diro.io']);
      if (typeof window !== 'undefined' && window.location?.origin) {
        allowedOrigins.add(window.location.origin);
      }
      if (!allowedOrigins.has(evt.origin)) return;
      const { progress_status, sessionid } = evt.data || {};
      if (!progress_status) return;
      const status = progress_status.toString().toLowerCase().trim();
      if (status === 'submitted') {
        setUnderReview(true);
        setStep(0);
        const normalizedSession = normalizeSessionId(sessionid);
        if (normalizedSession && lastSubmittedSessionRef.current !== normalizedSession) {
          lastSubmittedSessionRef.current = normalizedSession;
          axios.post('/bank_verification/manual_engagement', {
            identifier: identifier,
            session_id: normalizedSession
          }).catch(() => {
            // ignore errors here; backend under_review will still be updated by real callback if it arrives
          });
        }
      }
    };

    window.addEventListener('message', handleDiroMessage);
    return () => {
      window.removeEventListener('message', handleDiroMessage);
    };

  }, [identifier])

  const getIframe = useCallback(async () => {
    const params = new URLSearchParams({ identifier })
    if (isAffiliateMode) params.set('mode', 'affiliate')

    axios.get('/bank_verification/user_accounts?' + params.toString())
      .then(( { data } ) => {
        setCredentialLink(data.credential_url);
        setUploadLink(data.upload_url);
        setAccounts(data.accounts);
        setExternalIdentifier(data.external_identifier);
        setUnderReview(data.under_review);
      })
      .catch(( { response } ) => {
        if (response.data?.error){
          setErrorMsg(response.data.error)
        }
      })
  })

  if (errorMsg) {
    return (<div className="notification is-danger mt-5 is-centered">{errorMsg}</div>)
  }

  const submitAccounts = () =>{
    setStep(3)
    setShowSubmitBtn(false)
  }

  const normalizeSessionId = (raw) => {
    if (!raw) return null;
    const str = raw.toString().trim();
    const parts = str.split('sessionid=');
    if (parts.length > 1) {
      return parts.pop().replace(/[^a-zA-Z0-9\\-]/g, '');
    }
    return str.replace(/[^a-zA-Z0-9\\-]/g, '');
  };

  const hasAccounts = accounts.length > 0;
  const title = isAffiliateMode ? 'Verify your bank account' : 'Verify financial data via FundsV'
  const intro = 'You may choose to share a one-time snapshot of your recent data in one of two ways:'
  const retrieveLabel = isAffiliateMode
    ? 'Retrieve a recent bank statement from the bank source now'
    : 'Retrieve a recent statement from the bank source now'
  const affiliateDataPoints = [
    'Name on bank account',
    'Mailing address on the bank account',
    'Name of the bank',
    'Type of account',
    'Last few digits of the account number',
    'Period of the statement',
    'Timestamp of when the statement was retrieved'
  ]

  return(
    <>
      {step == 0 && 
        <section className="hero is-fullheight has-background-light">
          <div id="bg-overlay"></div>
          <div className="hero-body home-widget">
            <div className="card">
            {underReview && (
              <div
                style={{
                  backgroundColor: '#d8a000',
                  color: '#1f295b',
                  border: '1px solid #c38f00',
                  borderRadius: '4px',
                  padding: '7px 12px',
                  display: 'inline-block',
                  marginRight: '10px',
                }}
                title="For an explanation: https://fundsvalidator.com/faq/"
              >
                Data under review
              </div>
            )}
            {hasAccounts && (
              <a href={"/summary/" + identifier} target="_blank" className="button is-primary is-outlined">
                <span className="icon is-small">
                  <i className="fas fa-list"></i>
                </span>
                <span>View Summary</span>
              </a>
            )}
            
              <h1 className="has-text-weight-semibold is-size-4">{title}</h1>
              {externalIdentifier && <h2 className="has-text-weight-semibold is-size-5 mb-3">User Identifier: {externalIdentifier}</h2>}
              <div className="content has-text-center">
                {!isAffiliateMode && intro}

                <div className="verification-actions is-centered m-5">
                  <button disabled={!captureButtonId && !credentialLink} onClick={() => { setStep(1) }} className={`button is-info is-rounded ${(!captureButtonId && !credentialLink) && 'is-loading'}`}>
                    {retrieveLabel}
                  </button>
                  {!isAffiliateMode && (
                    <>
                      <div className="m-3">OR</div>
                      <button disabled={!uploadButtonId && !uploadLink} onClick={() => { setStep(2) }} className={`button is-link is-rounded color-1 ${(!uploadButtonId && !uploadLink) && 'is-loading'}`}>
                        Upload an original, digital PDF statement
                      </button>
                    </>
                  )}
                </div>
                {isAffiliateMode && (
                  <div className="has-text-left" style={{ display: 'inline-block', textAlign: 'left' }}>
                    <p className="mb-2">We will extract these data points:</p>
                    <ul>
                      {affiliateDataPoints.map((point) => <li key={point}>{point}</li>)}
                    </ul>
                    <p className="mt-3 mb-0">We will purge the data in seven (7) days.</p>
                  </div>
                )}
                <div
                  className='mt-5 has-text-centered'
                  style={{ fontSize: '14px', lineHeight: 1.35, color: '#47557a' }}
                >
                  Questions? Contact the referring source,
                  <br />
                  and review our <a href='https://fundsvalidator.com/faq/' target='_blank' rel='noopener noreferrer'>FAQ</a>,
                  <br />
                  <a href={legalUrl('/privacy/')} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>, <a href={legalUrl('/cookies/')} target='_blank' rel='noopener noreferrer'>Cookie Policy</a>, and <a href={legalUrl('/terms/')} target='_blank' rel='noopener noreferrer'>Terms of Use</a>.
                </div>
              </div>

            </div>
          </div>
        </section>
      }
      {step == 1 && (
        <CredentialWidget
          iframeUrl={credentialLink}
          onClose={() => { setStep(0) }}
          externalIdentifier={externalIdentifier}
          identifier={identifier}
          buttonId={captureButtonId}
          widgetScriptUrl={captureWidgetScriptUrl}
          widgetStylesheetUrl={captureWidgetStylesheetUrl}
        />
      )}
      {step == 2 && (
        <UploadWidget
          iframeUrl={uploadLink}
          onClose={() => { setStep(0) }}
          externalIdentifier={externalIdentifier}
          identifier={identifier}
          buttonId={uploadButtonId}
          widgetScriptUrl={uploadWidgetScriptUrl}
          widgetStylesheetUrl={uploadWidgetStylesheetUrl}
        />
      )}
    </>
  )
}

FundsVerificationWidget.propTypes = {
  identifier: PropTypes.string,
  uploadButtonId: PropTypes.string,
  uploadWidgetScriptUrl: PropTypes.string,
  uploadWidgetStylesheetUrl: PropTypes.string,
  captureButtonId: PropTypes.string,
  captureWidgetScriptUrl: PropTypes.string,
  captureWidgetStylesheetUrl: PropTypes.string,
  mode: PropTypes.string
};
export default FundsVerificationWidget
